import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDays, format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { usePost } from './../../apis/ApiRequests';
import { dateToString } from '../../utils/Library';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Paper, Box } from '@mui/material';
import { facilityData } from '../../Store';

const ITEMS_PER_PAGE = 15;
const PAGE_INTERVAL_MS = 30000;
const BIDDING_ACTIVE_MS = 30 * 60 * 1000;

const parseEndDateTime = (endDateTime) => {
    if (!endDateTime) return null;
    const str = String(endDateTime);
    if (str.length < 14) return null;
    return new Date(
        parseInt(str.slice(0, 4), 10),
        parseInt(str.slice(4, 6), 10) - 1,
        parseInt(str.slice(6, 8), 10),
        parseInt(str.slice(8, 10), 10),
        parseInt(str.slice(10, 12), 10),
        parseInt(str.slice(12, 14), 10)
    );
};

const isWithinBiddingPeriod = (endDateTime) => {
    const endDate = parseEndDateTime(endDateTime);
    if (!endDate) return false;
    const elapsed = Date.now() - endDate.getTime();
    return elapsed >= 0 && elapsed <= BIDDING_ACTIVE_MS;
};

const BidHistoryMonitor = () => {

	//===============================================
	// initialize
	//===============================================
	//-----------------------------------------------
    // initialize - セッション情報を取得
	//-----------------------------------------------
    const { authInfo }   = useAuth();
    const facilityCode   = authInfo.facilityCode ? authInfo.facilityCode : facilityData.facilityCode;
    const facilityName   = authInfo.facilityName;
    const userId         = authInfo.userId ? authInfo.userId : 'Signage';
    const luserName      = authInfo.userName;
    const authority      = authInfo.authority;
    const today          = format(addDays(new Date(), 0), 'yyyy-MM-dd');
    const navigate       = useNavigate();

	//-----------------------------------------------
    // initialize - useState
	//-----------------------------------------------
    const [ entryDate,   setEntryDate   ] = useState(today);
    const [ historyList, setHistoryList ] = useState([]);
    const [ currentPage, setCurrentPage ] = useState(1);
    const [ isBiddingActive, setIsBiddingActive ] = useState(false);

    const totalPages       = Math.max(1, Math.ceil(historyList.length / ITEMS_PER_PAGE));
    const effectivePage    = isBiddingActive ? 1 : currentPage;
    const displayTotalPages = isBiddingActive ? 1 : totalPages;
    const displayList = historyList.slice(
        (effectivePage - 1) * ITEMS_PER_PAGE,
        effectivePage * ITEMS_PER_PAGE
    );
    
	//===============================================
	// API
	//===============================================
    //-----------------------------------------------
    // API - searchDataByBidHistory / Signage
    //-----------------------------------------------
    const { response : searchData_history_res, post:searchData_history_post } = usePost();
    const searchData_history = async () => {
        const numericDate = dateToString(entryDate);
        const postData = {
            'className'    : 'mch.models.table.Signage',
            'methodName'   : 'searchDataByBidHistory',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'entryDate'    : numericDate,
        }
        await searchData_history_post(postData);
    };
	//===============================================
	// useEffect
	//===============================================
	//-----------------------------------------------
    // useEffect - ロード時の設定
	//-----------------------------------------------
    useEffect(() => {

        searchData_history();
        
        // 30秒ごとにデータを更新するタイマーを設定
        const interval = setInterval(timer, 10000);
    
        // コンポーネントのアンマウント時にタイマーをクリア
        return () => clearInterval(interval);
        
    },[]);
    const timer = () => {
        searchData_history();
    };
	//-----------------------------------------------
    // useEffect - searchDataByBidHistory / Signage
	//-----------------------------------------------
    useEffect(() => {
        if (!searchData_history_res) return;

        const data = searchData_history_res.Data;
        const list = Array.isArray(data) ? data : [];
        setHistoryList(list);

        const endDateTime = list[0]?.endDateTime ?? data?.endDateTime;
        setIsBiddingActive(isWithinBiddingPeriod(endDateTime));
    }, [searchData_history_res]);
	//-----------------------------------------------
    // useEffect - 入札中は1ページ目に固定
	//-----------------------------------------------
    useEffect(() => {
        if (isBiddingActive) {
            setCurrentPage(1);
        }
    }, [isBiddingActive]);
	//-----------------------------------------------
    // useEffect - ページ送り（30秒周期・入札中は停止）
	//-----------------------------------------------
    useEffect(() => {
        if (isBiddingActive) return;

        const interval = setInterval(() => {
            setCurrentPage(prev => (prev >= totalPages ? 1 : prev + 1));
        }, PAGE_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [totalPages, isBiddingActive]);
	//-----------------------------------------------
    // useEffect - データ更新時にページ番号を補正
	//-----------------------------------------------
    useEffect(() => {
        if (isBiddingActive) return;
        setCurrentPage(prev => (prev > totalPages ? 1 : prev));
    }, [totalPages, isBiddingActive]);

	//===============================================
	// eventHandler
	//===============================================
    const handleClick_header = () => {
        userId == 'Signage' ? navigate('/') : navigate('/signageMenu');
    };
    //===============================================
    // Css
    //===============================================
    const bidHistoryMonitor_table_cell_header = {
        backgroundColor: '#fff', color: '#005243', borderRight:'3px solid #005243', padding:'5px',  fontSize:'2em', fontWeight:'bold', height:'2em',
        borderBottom:'3px solid #005243', borderTop:'3px solid #005243'
    };
    const bidHistoryMonitor_table_cell = {
        padding:'5px', fontSize:'2em', fontWeight:'bold', 
        backgroundColor: '#005243', color: '#fff', borderBottom:'3px solid #fff', borderRight:'3px solid #fff'
    };
    
    //-----------------------------------------------
	// 正規表現でセパレート
    //-----------------------------------------------
	const separate = (num) => {
    	return String(num).replace( /(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
	};
	//===============================================
	// JSXのレンダー
	//===============================================
    return (
        <>
            <div className='bidHistoryMonitor signageContens' style={{ height: '100vh',overflow:'hidden' }}>
                <div  onClick={() => handleClick_header()} style={{backgroundColor:'#fff', paddingTop:'0.5em', paddingBottom:'0.5em'}}>
                    <div>
                        <span style={{fontSize:'2em', fontWeight:'bold', color:'#005243'}}>【落札情報】</span>
                        {isBiddingActive ? (
                            <span style={{fontSize:'2em', fontWeight:'bold', color:'#ff0000', marginLeft:'1em'}}>入札中</span>
                        ) : (
                            <span style={{fontSize:'2em', fontWeight:'bold', color:'#005243', marginLeft:'1em'}}>{ effectivePage }/{ displayTotalPages }</span>
                        )}
                    </div>
                    <div>
                        <span style={{fontSize:'2em', fontWeight:'bold', color:'#005243'}}>{ today }</span>
                    </div>
                </div>
                <div>
                    <TableContainer component={Paper} style={{border:'none', backgroundColor:'#efefef', marginTop:'0'}}>
                        <Table stickyHeader sx={{ minWidth: '500px', padding:0 }} size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={ bidHistoryMonitor_table_cell_header } style={{ width: '55vh', borderLeft:'3px solid #005243' }} align='center'>品名 / 漁法</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell_header } style={{ width: '13vh' }} align='center'>水槽</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell_header } style={{ width: '15vh' }} align='center'>Kg数</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell_header } style={{ width: '10vh' }} align='center'>数量</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell_header } style={{ width: '10vh' }} align='center'>単位</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell_header } style={{ width: '10vh' }} align='center'>コード</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell_header } style={{ width: '30vh' }} align='center'>屋号</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell_header } style={{ width: '15vh' }} align="center">入札単価</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell_header } style={{ width: '15vh' }} align="center">落札数量</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                            {displayList.map((row, index) => (
                                <TableRow key = { row.id } >
                                    <TableCell sx={ bidHistoryMonitor_table_cell } align="left" style={{borderLeft:'3px solid #fff'}}>{ row.displayGyoshu }</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell } align="center">{ row.danbe }</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell } align="right">{ row.kgSuryo }</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell } align="right">{ row.suryo }</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell } align="left">{ row.kgKubunName }</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell } align="right">{ row.traderCode }</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell } align="left">{ row.trader }</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell } align="right">{row.tanka ? separate(row.tanka) + "円" : ""}</TableCell>
                                    <TableCell sx={ bidHistoryMonitor_table_cell } align="right">{ row.entrySuryo }</TableCell>
                                </TableRow>
                            ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </div>
        </>
    )
}
export default BidHistoryMonitor;